import React, { useEffect } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { AlignItems, Box, Display, FlexDirection } from '@gousto-internal/citrus-react'

import { CollectionDescription, CollectionTitle, RecipeCount } from '@library/menu-page'
import type { MenuApiPreferencesResponse } from '@library/menu-service'

import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useCollectionHeaders } from 'routes/Menu/MenuRecipesPage/CollectionHeader/CollectionHeaderWrapper/hooks'
import { useShouldRemoveMerchandisingBanner } from 'routes/Menu/MenuRecipesPage/CollectionHeader/useShouldRemoveMerchandisingBanner'
import { useBasketDelivery } from 'routes/Menu/domains/basket/internal/delivery'
import { useBrandHeadersInfo } from 'routes/Menu/domains/brand'
import { CollectionId } from 'routes/Menu/domains/collections'

import { CollectionHeader } from './CollectionHeader/CollectionHeader'
import { RecipeGrid } from './RecipeGrid/RecipeGrid'

type CollectionScreenProps = {
  menuPreferences?: MenuApiPreferencesResponse
  userStatus?: string
  isFlexOrder?: boolean
  isFeaturedRangeMerchandisingEnabled?: boolean
}

const CATEGORY_DESCRIPTION: Record<string, { title: string; subtitle: string }> = {
  default: {
    title: "Sorted by what's relevant to you.",
    subtitle: "Based on what you've had before.",
  },
  [CollectionId.Recommendations]: {
    title: "Based on what you've had before.",
    subtitle: "We think you'll love these recipes.",
  },
}

export const CollectionScreen = ({
  menuPreferences,
  userStatus = '',
  isFlexOrder = false,
  isFeaturedRangeMerchandisingEnabled,
}: CollectionScreenProps) => {
  void isFeaturedRangeMerchandisingEnabled

  const currentCollectionId = useCurrentCollectionId()
  const { shortTitle, recipes } = useCollection(currentCollectionId) ?? {
    shortTitle: '',
    recipes: [],
  }

  const delivery = useBasketDelivery()
  const { headers, collectionsPerMenu, isLoading: isBrandHeadersLoading } = useBrandHeadersInfo()
  const collectionsHeaders = useCollectionHeaders(headers, collectionsPerMenu)
  const removeMerchandisingBanner = useShouldRemoveMerchandisingBanner()
  const showMerchandisingBanner = Boolean(collectionsHeaders) && !removeMerchandisingBanner
  const showAccountOnHoldAlert = userStatus === 'onhold'

  const { title, subtitle } =
    CATEGORY_DESCRIPTION[currentCollectionId] ?? CATEGORY_DESCRIPTION.default

  const merchandisingBannerShown =
    showMerchandisingBanner && !isFlexOrder && !showAccountOnHoldAlert

  useEffect(() => {
    if (isBrandHeadersLoading) {
      return
    }

    datadogRum?.addAction('view_collection_screen', {
      deliveryDate: delivery.date,
      collectionId: currentCollectionId,
      recipeCount: recipes.length,
      merchandisingBannerShown,
    })
  }, [
    currentCollectionId,
    recipes.length,
    merchandisingBannerShown,
    delivery.date,
    isBrandHeadersLoading,
  ])

  return (
    <>
      <CollectionHeader
        showAccountOnHoldAlert={showAccountOnHoldAlert}
        isFlexOrder={isFlexOrder}
        showMerchandisingBanner={showMerchandisingBanner}
        merchandisingHeader={collectionsHeaders}
      />
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
      >
        <Box maxWidth="1600px" width="100%">
          <Box paddingH={[3, 3, 0]} paddingBottom={2}>
            {(!showMerchandisingBanner || showAccountOnHoldAlert || isFlexOrder) && (
              <CollectionTitle text={shortTitle} />
            )}
            <CollectionDescription title={title} subtitle={subtitle} />
            <RecipeCount count={recipes.length} />
          </Box>
          <RecipeGrid menuPreferences={menuPreferences} />
        </Box>
      </Box>
    </>
  )
}
